// src/core/common/modal/registerModal.tsx
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import axios from "axios";
import API_URLS from "../apiUrls";
import { showModal, hideModal, swapModals } from "../modalHelpers";

type PasswordField = "password" | "confirmPassword";

const RegisterModal: React.FC = () => {
  const [formData, setFormData] = useState({
    first_name: "", last_name: "", email: "", phone: "", password: "", confirmPassword: "",
  });
  const [otp, setOtp] = useState("");
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [passwordVisibility, setPasswordVisibility] = useState({ password: false, confirmPassword: false });
  const otpInputRef = useRef<HTMLInputElement | null>(null);

  // Ensure no leftover modal-open when this component unmounts
  useEffect(() => {
    return () => {
      document.body.classList.remove("modal-open");
    };
  }, []);

  const togglePasswordVisibility = (f: PasswordField) =>
    setPasswordVisibility((p) => ({ ...p, [f]: !p[f] }));

  const isValidPhoneNumber = (raw: string) => {
    if (!raw) return false;
    const digits = raw.replace(/\D/g, "");
    return /^[1-9][0-9]{6,14}$/.test(digits);
    // E.164-ish: 7–15 digits, no leading 0
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setSuccess(null);

    if (!formData.first_name || !formData.last_name || !formData.email)
      return setError("Please complete the required fields.");
    if (!isValidPhoneNumber(formData.phone))
      return setError("Please enter a valid phone number.");
    if (formData.password.length < 8)
      return setError("Password must be at least 8 characters.");
    if (!/[A-Z]/.test(formData.password) || !/[0-9]/.test(formData.password))
      return setError("Password must contain at least one uppercase letter and one number.");
    if (formData.password !== formData.confirmPassword)
      return setError("Passwords do not match.");

    try {
      setLoading(true);
      const phone = formData.phone.startsWith("+") ? formData.phone : "+" + formData.phone;
      const res = await axios.post(`${API_URLS.AUTH.REGISTER}`, {
        first_name: formData.first_name, last_name: formData.last_name,
        email: formData.email, phone, password: formData.password,
        user_type: "customer", otp_channel: "email",
      });
      const message = (res.data?.message || "").toString();
      if (message.toLowerCase().includes("otp")) {
        setSuccess("✅ OTP sent. Please verify to complete registration.");
        hideModal("register-modal");
        setShowOtpModal(true);
        setTimeout(() => otpInputRef.current?.focus(), 80);
      } else {
        setSuccess(message || "Registered. Please verify OTP.");
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || "Registration failed.";
      if (String(msg).toLowerCase().includes("exists"))
        setError("Email or phone already registered. Please sign in.");
      else setError(String(msg));
    } finally { setLoading(false); }
  };

  const handleOtpVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setSuccess(null);
    if (!otp.trim()) return setError("Please enter the OTP.");

    try {
      setOtpLoading(true);
      const res = await axios.post(`${API_URLS.AUTH.VERIFY_OTP}`, { email: formData.email, otp: otp.trim() });
      const msg = (res.data?.message || "").toString();
      if (msg.toLowerCase().includes("success") || msg.toLowerCase().includes("verified")) {
        setSuccess("🎉 Verified! Please sign in.");
        setShowOtpModal(false);
        setTimeout(() => showModal("login-modal"), 350);
      } else setError(msg || "OTP verification failed.");
    } catch (err: any) {
      setError(err?.response?.data?.message || "OTP verification failed.");
    } finally { setOtpLoading(false); }
  };

  const handleResendOtp = async () => {
    setError(null); setSuccess(null);
    if (!formData.email) return setError("Email is required to resend OTP.");
    try {
      setResendLoading(true);
      const res = await axios.post(`${API_URLS.AUTH.RESEND_OTP}`, { email: formData.email });
      setSuccess(res.data?.message || "OTP resent.");
      setTimeout(() => otpInputRef.current?.focus(), 80);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to resend OTP.");
    } finally { setResendLoading(false); }
  };

  // Click-outside fallback when Bootstrap JS isn't present
  const onBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (!(window as any).bootstrap?.Modal) {
      if (target.classList.contains("modal")) hideModal("register-modal");
    }
  };

  return (
    <>
      {/* REGISTER MODAL */}
      <div
        className="modal fade"
        id="register-modal"
        tabIndex={-1}
        aria-hidden="true"
        data-bs-backdrop="true"
        data-bs-keyboard="true"
        onMouseDown={onBackdropClick}
      >
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "90vw", justifyContent: "center" }}>
          <div className="modal-content border-0 rounded-4 shadow-lg overflow-hidden" style={{ height: "90vh" }}>
            <div className="row g-0 h-100">
              {/* LEFT: full-bleed image (served from public/) */}
              <div className="col-lg-6 d-none d-lg-block h-100">
                <img
                  src="/assets/img/banner/loginPage.png"
                  alt="Register banner"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
              </div>

              {/* RIGHT: centered title + form */}
              <div className="col-lg-6 d-flex h-100 position-relative">
                {/* Close (X) — works with & without Bootstrap JS */}
                <button
                  type="button"
                  className="btn p-0 position-absolute"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  style={{ top: 16, right: 20 }}
                  onClick={() => hideModal("register-modal")}
                >
                  <i className="ti ti-x fs-20" />
                </button>

                <div className="d-flex flex-column justify-content-center align-items-center w-100 px-4 px-lg-5">
                  <h3 className="fw-semibold text-center mb-4" style={{ letterSpacing: "0.3px" }}>
                    Create account
                  </h3>

                  <form onSubmit={handleRegister} noValidate style={{ width: "100%", maxWidth: 560 }}>
                    <div className="row">
                      <div className="col-md-6 mb-3">
                        <label className="form-label">First Name</label>
                        <input
                          type="text" name="first_name"
                          className="form-control form-control-lg"
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="First name" required
                        />
                      </div>
                      <div className="col-md-6 mb-3">
                        <label className="form-label">Last Name</label>
                        <input
                          type="text" name="last_name"
                          className="form-control form-control-lg"
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Last name" required
                        />
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input
                        type="email" name="email"
                        className="form-control form-control-lg"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required autoComplete="email"
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Phone Number</label>
                      <PhoneInput
                        country={"in"}
                        value={formData.phone}
                        onChange={(phone) => setFormData({ ...formData, phone })}
                        inputProps={{ name: "phone", required: true }}
                        inputClass="form-control form-control-lg w-100"
                        buttonClass="border-end-0"
                        containerClass="w-100"
                      />
                    </div>

                    {(["password", "confirmPassword"] as PasswordField[]).map((field) => (
                      <div className="mb-3" key={field}>
                        <label className="form-label">{field === "password" ? "Password" : "Confirm Password"}</label>
                        <div className="input-group">
                          <input
                            type={passwordVisibility[field] ? "text" : "password"}
                            name={field}
                            className="form-control form-control-lg"
                            placeholder={field === "password" ? "Enter password" : "Confirm password"}
                            value={(formData as any)[field]}
                            onChange={handleChange}
                            required
                          />
                          <button
                            type="button"
                            className="input-group-text bg-white"
                            onClick={() => togglePasswordVisibility(field)}
                          >
                            <i className={`isax ${passwordVisibility[field] ? "isax-eye" : "isax-eye-slash"}`} />
                          </button>
                        </div>
                      </div>
                    ))}

                    {error && <p className="text-danger text-center mb-2">{error}</p>}
                    {success && <p className="text-success text-center mb-2">{success}</p>}

                    <button type="submit" className="btn btn-primary btn-lg w-100 rounded-pill" disabled={loading}>
                      {loading ? "Registering..." : "Register"}
                    </button>

                    {/* Switch back to Login */}
                    <p className="text-center mt-4 mb-0">
                      Already have an account?{" "}
                      <Link
                        to="#"
                        className="link-primary fw-semibold"
                        onClick={(e) => { e.preventDefault(); swapModals("register-modal", "login-modal"); }}
                      >
                        Sign in
                      </Link>
                    </p>
                  </form>
                </div>
              </div>
            </div>
            {/* /row */}
          </div>
        </div>
      </div>

      {/* OTP Modal (React-rendered, simple custom modal) */}
      {showOtpModal && (
        <>
          {/* Manual backdrop for OTP — removed automatically when we hide it */}
          <div className="modal-backdrop fade show" />
          <div className="modal fade show" id="otp-modal" role="dialog" aria-modal="true" style={{ display: "block", zIndex: 2000 }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content rounded-4 shadow">
                <div className="modal-header border-0">
                  <h6 className="m-0">Verify OTP</h6>
                  <button type="button" className="btn-close" onClick={() => setShowOtpModal(false)} />
                </div>
                <div className="modal-body p-4">
                  <p className="text-muted mb-3">OTP sent to <b>{formData.email || "(email)"}</b></p>
                  <form onSubmit={handleOtpVerify}>
                    <input
                      ref={otpInputRef}
                      type="text"
                      maxLength={6}
                      className="form-control form-control-lg text-center mb-3"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      required
                      autoFocus
                      style={{ letterSpacing: "6px" }}
                    />
                    {error && <p className="text-danger">{error}</p>}
                    {success && <p className="text-success">{success}</p>}

                    <button type="submit" className="btn btn-primary w-100 mb-2" disabled={otpLoading}>
                      {otpLoading ? "Verifying..." : "Verify OTP"}
                    </button>
                    <button type="button" className="btn btn-link text-decoration-none w-100" onClick={handleResendOtp} disabled={resendLoading}>
                      {resendLoading ? "Resending..." : "Resend OTP"}
                    </button>
                    <button type="button" className="btn btn-outline-secondary w-100 mt-2" onClick={() => { setShowOtpModal(false); showModal("register-modal"); }}>
                      Edit details
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default RegisterModal;
